import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, ScanLine, X, Camera } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { ensureItemIdPrefix, ITEM_ID_PREFIX } from '../../lib/itemId';

const SCANNER_ID = 'qr-reader';

export const SearchItem = () => {
  const navigate = useNavigate();
  const [itemId, setItemId] = useState(ITEM_ID_PREFIX);
  const [touched, setTouched] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [camError, setCamError] = useState('');
  const scannerRef = useRef(null);
  const hasItemIdValue = Boolean(itemId.trim()) && itemId.trim().toUpperCase() !== ITEM_ID_PREFIX;

  // Start / stop scanner when `scanning` changes
  useEffect(() => {
    if (!scanning) return;
    setCamError('');

    const qr = new Html5Qrcode(SCANNER_ID);
    scannerRef.current = qr;

    qr.start(
      { facingMode: 'environment' },
      { fps: 12, qrbox: { width: 240, height: 240 } },
      (decoded) => {
        const id = ensureItemIdPrefix(decoded);
        qr.stop().catch(() => {});
        scannerRef.current = null;
        setScanning(false);
        setItemId(id);
        navigate(`/admin/inventory/items/${encodeURIComponent(id)}`);
      },
      () => { /* per-frame errors — ignore */ }
    ).catch(() => {
      setCamError('Camera access was denied or is unavailable. Please allow camera permission and try again.');
      setScanning(false);
    });

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [scanning, navigate]);

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const id = itemId.trim();
    if (!id || !hasItemIdValue) { setTouched(true); return; }
    navigate(`/admin/inventory/items/${encodeURIComponent(id)}`);
  };

  return (
    <div data-testid="search-item-page" className="min-h-[60vh] flex flex-col items-center justify-center">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Search className="h-8 w-8 text-orange-500" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Search Item</h1>
          <p className="text-slate-500 mt-2 text-sm">Enter an Item ID or scan its QR code with your camera</p>
        </div>

        {/* Card */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">

          {/* Manual input */}
          <form onSubmit={handleSubmit}>
            <label className="block text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Item ID
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={itemId}
                onChange={e => { setItemId(e.target.value); setTouched(false); }}
                onBlur={() => setTouched(true)}
                placeholder="e.g. ITEM-ABC123"
                autoFocus={!scanning}
                data-testid="item-id-input"
                className={`flex-1 px-4 py-3 border rounded-sm text-sm font-mono focus:outline-none transition-colors ${
                  touched && !hasItemIdValue
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-slate-200 focus:border-orange-500'
                }`}
              />
              <button
                type="submit"
                data-testid="search-item-btn"
                className="px-5 py-3 bg-orange-500 text-white font-bold rounded-sm hover:bg-orange-600 transition-colors inline-flex items-center gap-2"
              >
                Go <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            {touched && !hasItemIdValue && (
              <p className="text-red-500 text-xs mt-1.5">Please enter an Item ID</p>
            )}
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Scan QR button / scanner */}
          {!scanning ? (
            <button
              type="button"
              onClick={() => setScanning(true)}
              data-testid="scan-qr-btn"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-dashed border-orange-300 text-orange-600 font-semibold rounded-sm hover:bg-orange-50 hover:border-orange-400 transition-colors"
            >
              <Camera className="h-5 w-5" />
              Scan QR with Camera
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <ScanLine className="h-4 w-4 text-orange-500 animate-pulse" />
                  Point camera at QR code…
                </div>
                <button
                  type="button"
                  onClick={stopScanner}
                  data-testid="cancel-scan-btn"
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-500 font-medium transition-colors"
                >
                  <X className="h-4 w-4" /> Cancel
                </button>
              </div>
              {/* html5-qrcode mounts the camera feed into this div */}
              <div
                id={SCANNER_ID}
                className="w-full rounded-sm overflow-hidden border border-slate-200"
                style={{ minHeight: '280px' }}
              />
            </div>
          )}

          {/* Camera error */}
          {camError && (
            <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm px-3 py-2">
              <X className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              {camError}
            </div>
          )}

          <p className="text-slate-400 text-xs mt-4">
            Tip: the Item ID is printed below every QR label and also visible in the Inventory detail page.
          </p>
        </div>
      </div>
    </div>
  );
};
